import React, { useEffect, useRef, useState } from "react";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { useNavigate, useParams } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import moment from "moment";
import { AddButton } from "../../../utils/Custom/Button";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../../global";
import { toast } from "react-toastify";
import loginBg from "../../image/loginbackground.png";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { generateApplicationFormPDF } from "./applicationFormPDF";

const InfoItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-md font-bold text-gray-800">{label}</span>
    <span className="text-gray-800">{value || "-"}</span>
  </div>
);

const formatDate = (value) => {
  if (!value || !moment(value).isValid()) return "-";
  return moment(value).format("DD/MM/YYYY");
};

const EmployeeView = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const permissions = getPermissions(props.moduleId);
  const [inputStates, setInputStates] = React.useState({});
  const [empList, setempList] = React.useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchEmployeesDetails = async () => {
    try {
      const [employee] = await fetchQuery(`select * from employees where id = ${params.id}`);

      const detailTables = [
        "employeeQualification",
        "employeeExperience",
        "employeeNominee",
        "employeeEmergencyDetails",
        "employeeOtherData",
        "employeeDocuments",
      ];

      const results = await Promise.all(
        detailTables.map((table) =>
          fetchQuery(`SELECT * FROM ${table} WHERE employeeId = ${params.id}`)
        )
      );

      const mergedData = { ...employee };

      detailTables.forEach((table, index) => {
        const record = results[index]?.[0];
        if (record) {
          Object.entries(record).forEach(([key, value]) => {
            if (key === "id") {
              mergedData[`${table?.split("/")?.[1]}Id`] = value;
            } else if (!(key in mergedData)) {
              mergedData[key] = value;
            }
          });
        }
      });
      mergedData.experienceList = results[1] || [];
      mergedData.familyMembers = results[2] || [];

      const prevHistRecords = await fetchQuery(
        `SELECT * FROM employeePreviousHistory WHERE employeeId = ${params.id}`
      );
      mergedData.referees = (prevHistRecords || []).map((r) => ({
        id: r.id,
        name: r.Name || "",
        contactNo: r.contactNo || "",
        position: r.positionHeld || "",
      }));
      if (prevHistRecords && prevHistRecords.length > 0) {
        const firstRec = prevHistRecords[0];
        mergedData.agreeRefereesContact = firstRec.refereeContacted || firstRec.refereesContacted || mergedData.agreeRefereesContact;
        mergedData.workTypeAvailable = firstRec.workTypeAvailable || mergedData.workTypeAvailable;
        mergedData.noticePeriodOrJoiningDate = firstRec.joiningNoticeDate || firstRec.noticePeriodOrJoiningDate || mergedData.noticePeriodOrJoiningDate;
        mergedData.terminatedPastEmployer = firstRec.terminatedEmployer || firstRec.terminatedPastEmployer || mergedData.terminatedPastEmployer;
        mergedData.connectedWithBusiness = firstRec.connectedBusiness || firstRec.connectedWithBusiness || mergedData.connectedWithBusiness;
        mergedData.ownHouseArrangement = firstRec.ownHouseArrangement || mergedData.ownHouseArrangement;
        mergedData.serveIndiaAbroad = firstRec.serveIndiaAbroad || mergedData.serveIndiaAbroad;
        mergedData.friendsRelativesInDYC = firstRec.friendsInDYC || firstRec.friendsRelativesInDYC || mergedData.friendsRelativesInDYC;
        mergedData.whoReferredDYC = firstRec.whoReferredDYC || mergedData.whoReferredDYC;
        mergedData.interviewedBeforeDYC = firstRec.interviewedDYC || firstRec.interviewedBeforeDYC || mergedData.interviewedBeforeDYC;
        mergedData.interviewedBeforeDYCDate = firstRec.interviewedDYCDate || firstRec.interviewedBeforeDYCDate || mergedData.interviewedBeforeDYCDate;
        mergedData.relativeInGovtOrg = firstRec.relativeGovtOrg || firstRec.relativeInGovtOrg || mergedData.relativeInGovtOrg;
        mergedData.relativeInGovtOrgDetails = firstRec.relativeGovtDetails || firstRec.relativeInGovtOrgDetails || mergedData.relativeInGovtOrgDetails;
        mergedData.relativeOwnBusiness = firstRec.relativeBusiness || firstRec.relativeOwnBusiness || mergedData.relativeOwnBusiness;
        mergedData.relativeOwnBusinessDetails = firstRec.relativeBusinessDetails || firstRec.relativeOwnBusinessDetails || mergedData.relativeOwnBusinessDetails;
        mergedData.potentialConflictOfInterest = firstRec.conflictOfInterest || firstRec.potentialConflictOfInterest || mergedData.potentialConflictOfInterest;
      }

      // Fetch linked offer, candidate, pipeline and interview schedule details using optimized batching
      if (mergedData.offerId) {
        try {
          const safeName = (mergedData.name || "").replace(/'/g, "''");
          const [offerRec, intRec] = await Promise.all([
            fetchQuery(
              `SELECT o.offerDate, o.ctcOffered, o.confirmedJoiningDate, o.actualJoiningDate, o.pipelineId, o.position AS offerPosition,
                      c.certifications, c.noticePeriod, c.sourceChannel, c.notes AS candNotes, c.dateAdded AS candDate,
                      p.interviewer AS pipeInterviewer, p.interviewDate AS firstIntDate, p.rejectionDetails AS pipeRemarks, p.position AS pipePosition
               FROM talentAcqOffer o 
               LEFT JOIN talentAcqPipeline p ON (o.pipelineId = p.id OR o.pipelineId = p.pipelineId)
               LEFT JOIN talentAcqCandidate c ON (p.candId = c.id OR p.candId = c.CandId) 
               WHERE o.offerId = '${mergedData.offerId}' OR o.id = '${mergedData.offerId}'`
            ).then((res) => res?.[0]),
            fetchQuery(
              `SELECT interviewDate, Interviewer, notes 
               FROM talentAcqInterview 
               WHERE candidateName = '${safeName}' OR pipelineId IN (
                 SELECT id FROM talentAcqPipeline WHERE candidateName = '${safeName}'
               )
               ORDER BY round DESC, id DESC LIMIT 1`
            ).then((res) => res?.[0]),
          ]);

          if (offerRec) {
            mergedData.offerDate = offerRec.offerDate;
            mergedData.ctcOffered = offerRec.ctcOffered || mergedData.ctcOffered;
            mergedData.certifications = offerRec.certifications || mergedData.certifications;
            mergedData.noticePeriod = offerRec.noticePeriod || mergedData.noticePeriod;
            mergedData.dateReportingToWork = offerRec.actualJoiningDate || offerRec.confirmedJoiningDate || mergedData.dateOfJoining;
            mergedData.actualJoiningDate = offerRec.actualJoiningDate || offerRec.confirmedJoiningDate || mergedData.actualJoiningDate;

            if (offerRec.offerPosition || offerRec.pipePosition) {
              mergedData.hrDesignation = offerRec.offerPosition || offerRec.pipePosition || mergedData.hrDesignation;
              mergedData.designation = offerRec.offerPosition || offerRec.pipePosition || mergedData.designation;
            }

            mergedData.interviewedBy = offerRec.pipeInterviewer || (intRec ? intRec.Interviewer : "") || mergedData.interviewedBy || "";
            mergedData.firstInterviewDate = offerRec.firstIntDate || offerRec.candDate || mergedData.firstInterviewDate || "";
            mergedData.hrRemarks = offerRec.pipeRemarks || (intRec ? intRec.notes : "") || offerRec.candNotes || mergedData.hrRemarks || mergedData.remarks || "";
          }

          if (intRec) {
            mergedData.secondInterviewDate = intRec.interviewDate || mergedData.secondInterviewDate || "";
            if (!mergedData.interviewedBy && intRec.Interviewer) {
              mergedData.interviewedBy = intRec.Interviewer;
            }
          }
        } catch (e) {
          console.error("Error enriching offer details in employeeView:", e);
        }
      }

      setInputStates(mergedData);
    } catch (error) {
      console.error("Error fetching employee details:", error);
    }
  };

  const allEmployeeList = async () => {
    try {
      const employees = await fetchQuery(`select id, employeesCode, name from employees`);
      setempList(employees);
    } catch (error) {
      console.error("Error fetching allEmployee list:", error);
    }
  };

  useEffect(() => {
    allEmployeeList();
  }, []);

  useEffect(() => {
    if (empList.length > 0) {
      fetchEmployeesDetails();
    }
  }, [empList, params.id]);

  const getEmployeeNameById = (id) => {
    return empList?.find((emp) => emp.id == id)?.name || "";
  };

  const handleViewMore = () => {
    navigate(`/master/employeeMasterEdit/${params.id}`);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && /[&#$*]/.test(file.name)) {
      toast.warning("File name contains special characters. Please rename the file and upload again.");
      return;
    }
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      alert("File size should be less than 3MB");
      return;
    }
    setIsUploading(true);

    try {
      const data = new FormData();
      data.append("fileToUpload", file);

      const response = await axiosWithToken.post(BASE_URL + `fileUpload/fileUpload.php`, data);

      await axiosWithToken.patch(BASE_URL + `employees/${params.id}`, {
        profileImageUlr: response?.data || "",
      });

      setInputStates((prev) => ({
        ...prev,
        profileImageUlr: response?.data || "",
      }));
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getInitials = () => {
    return (inputStates?.name || "")
      ?.split(" ")
      ?.filter(Boolean)
      ?.slice(0, 2)
      .map((word) => word[0])
      ?.join("")
      .toUpperCase();
  };

  return (
    <>
      {/* Full page bg */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, backgroundImage: `url(${loginBg})`, backgroundSize: "cover", backgroundPosition: "center" }} />

      <div className="max-w-7xl mx-auto p-4" style={{ position: "relative", zIndex: 10 }}>
        {/* Card */}
        <div className="shadow-lg rounded-3xl p-6 space-y-6" style={{ backgroundColor: "rgba(255,255,255,0.92)" }}>
          {/* Header: Profile */}
          <div className="flex items-center space-x-4">
            <div className="relative group">
              {/* Avatar Container */}
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-xl font-semibold text-gray-600 overflow-hidden">
                {inputStates?.profileImageUlr ? (
                  <img
                    src={inputStates.profileImageUlr}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>{getInitials()}</span>
                )}
              </div>

              {/* Camera Overlay */}
              <div
                onClick={triggerFileInput}
                className="absolute inset-0 w-16 h-16 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center cursor-pointer transition-all duration-200"
              >
                <svg
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>

              {/* Loading Spinner */}
              {isUploading && (
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {inputStates?.name}
              </h2>
              <p className="text-sm text-gray-500">
                Employee Code: {inputStates?.employeesCode}
              </p>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoItem label="Address" value={inputStates?.currentAddress} />
            <InfoItem label="Department" value={inputStates?.department} />
            <InfoItem label="Designation" value={inputStates?.hrDesignation || inputStates?.designation} />
            <InfoItem label="Contact Number" value={inputStates?.contactNumber} />
            <InfoItem label="Personal Email" value={inputStates?.emailAddress} />
            <InfoItem label="Office Email" value={inputStates?.officeEmailAddress} />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
            <InfoItem label="Date of Birth" value={formatDate(inputStates?.dateOfBirth)} />
            <InfoItem label="Date of Joining" value={formatDate(inputStates?.dateOfJoining)} />
            <InfoItem label="Gender" value={inputStates?.gender} />
          </div>

          {/* Reporting Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
            <InfoItem label="Reporting Manager" value={getEmployeeNameById(inputStates?.reportingManagerId)} />
            <InfoItem label="Approving Manager" value={getEmployeeNameById(inputStates?.appprovalManagerId)} />
          </div>

          {/* Bottom Actions */}
          <div className="flex justify-end pt-4 space-x-3">
            <button
              onClick={async () => {
                try {
                  const pdfDoc = await generateApplicationFormPDF(inputStates || {});
                  const fileName = `Application_Form_${inputStates?.name || "Employee"}.pdf`;
                  pdfDoc.save(fileName);
                } catch (err) {
                  console.error("Error generating application form PDF:", err);
                  toast.error("Failed to generate Application Form PDF");
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow flex items-center space-x-2 transition"
            >
              <PictureAsPdfIcon fontSize="small" />
              <span>Application Form PDF</span>
            </button>
            <AddButton
              id="btn-add-outline"
              label=" View More +"
              onClick={handleViewMore}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeView;